import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
  Button,
  csx,
  FilterFooter,
  FilterListbox,
  FilterOptionCheckbox,
  FilterPopover,
  Search,
  Stack,
  type UseFilterMultipleReturn,
  useSearchState,
} from '@vtex/admin-ui'
import { BaseFilter } from '@/components/filters/BaseFilter/BaseFilter'
import { useFilterMultiple } from '@/components/filters/hooks/useFilterMultiple'
import { useSellerWithPagination } from '@/hooks/useSeller'

import * as styles from './FilterSeller.styles'

interface FilterSellerProps<T> {
  state: UseFilterMultipleReturn<T>
}

function FilterSellerPopover<T>(props: FilterSellerProps<T>) {
  const { state } = props
  const { formatMessage } = useIntl()

  const {
    getInputProps,
    debouncedValue: debouncedSellerFilter,
    value: sellerFilter,
  } = useSearchState({
    timeout: 500,
  })

  const {
    sellers = [],
    loadMore,
    hasMore,
    isLoading,
  } = useSellerWithPagination(debouncedSellerFilter)

  useEffect(() => {
    if (isLoading) return state.setStatus('loading')

    if (!sellers.length) return state.setStatus('not-found')

    state.setStatus('ready')
  }, [isLoading, sellers.length, state])

  return (
    <FilterPopover state={state}>
      <Stack className={styles.sellerFilterHeader}>
        <Search
          {...getInputProps()}
          loading={sellerFilter !== debouncedSellerFilter}
        />
      </Stack>
      <FilterListbox>
        {sellers.map((seller) => (
          <FilterOptionCheckbox
            key={seller.id}
            id={seller.id}
            label={seller.name}
          />
        ))}
        {hasMore ? (
          <Button
            onClick={loadMore}
            variant="neutralTertiary"
            className={csx({ width: '100%' })}
            disabled={isLoading}
          >
            {formatMessage({
              id: 'admin/admin.catalog.brand-filter-pagination-label',
              defaultMessage: 'See more',
            })}
          </Button>
        ) : null}
      </FilterListbox>
      <FilterFooter />
    </FilterPopover>
  )
}

export type SerializedData = string[]

export function FilterSeller(props: { name: string }) {
  const { formatMessage } = useIntl()

  return (
    <BaseFilter
      disclosureMessage={formatMessage({
        id: 'admin/admin.catalog.products-filter-seller',
        defaultMessage: 'Seller',
      })}
      FilterPopoverComponent={FilterSellerPopover}
      filterName={props.name}
      useFilterState={useFilterMultiple}
      toSerializableData={(data) =>
        (data.appliedItems.map((item) => item.id) || []) as SerializedData
      }
    />
  )
}
